import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { PROFILE_PAGE_PENDING_APPROVAL_VARIANT } from '../../util/urlHelpers';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { 
  getUserIdByUsername, 
  getArtistUsernamesData, 
  fetchArtistUsernames,
  isArtistUsernamesCacheValid 
} from '../../ducks/artistUsernames.duck';

const { UUID } = sdkTypes;

// Function to get username-to-ID mapping from Redux store
const getUsernameToIdMappingFromStore = (dispatch, getState, forceRefresh = false) => {
  const state = getState();
  const cacheValid = isArtistUsernamesCacheValid(state);

  if (!forceRefresh && cacheValid) {
    const data = getArtistUsernamesData(state);
    if (data && Object.keys(data).length > 0) {
      console.log('Using cached username mapping from Redux store');
      return Promise.resolve({ data });
    }
  }

  // If cache is invalid or we need fresh data, dispatch fetch
  console.log('Fetching fresh username mapping');
  return dispatch(fetchArtistUsernames(forceRefresh)).then(() => {
    const updatedState = getState();
    const data = getArtistUsernamesData(updatedState);
    return { data };
  });
};

// Function to clear the username cache (now handled by Redux)
export const clearUsernameCache = () => dispatch => {
  // The cache is now managed by Redux, so we can force a refresh
  return dispatch(fetchArtistUsernames(true));
};

// Function to force refresh the username cache
export const refreshUsernameCache = () => (dispatch, getState) => {
  return getUsernameToIdMappingFromStore(dispatch, getState, true).then(() => {
    console.log('Username cache refreshed');
    const state = getState();
    return { data: getArtistUsernamesData(state) };
  });
};

// ================ Async Thunks ================ //

///////////////
// Show User //
///////////////
const showUserPayloadCreator = ({ userId, config }, { dispatch, rejectWithValue, extra: sdk }) => {
  return sdk.users
    .show({
      id: userId,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const userFields = config?.user?.userFields;
      const sanitizeConfig = { userFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return response;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const showUserThunk = createAsyncThunk('ProfilePage/showUser', showUserPayloadCreator);

// Backward compatible wrapper for the thunk
export const showUser = (userId, config) => dispatch => {
  return dispatch(showUserThunk({ userId, config }));
};

///////////////////////////////////
// Query User Listings By Username //
///////////////////////////////////
export const queryUserListingsByUsername = (username, config) => (dispatch, getState, sdk) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      pub_artistUsername: username,
      pub_isVariant: false,
      sort: 'pub_isFeatured',
      include: ['images', 'author', 'author.profileImage', 'author.displayName'],
      'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    })
    .then(response => {
      const userId = response.data.data[0]?.relationships?.author?.data;
      if (userId) {
        dispatch(showUser(userId.id, config));
        dispatch(queryUserReviews(userId.id));
      }

      dispatch(addMarketplaceEntities(response));
      return response;
    });
};

/////////////////////////
// Query User Listings //
/////////////////////////
const queryUserListingsPayloadCreator = (
  { userId, config, ownProfileOnly = false },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const queryParams = {
    include: ['author', 'images'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    // Filter out variant listings to show only main listings
    pub_isVariant: false,
    sort: 'pub_isFeatured',
  };

  const listingsPromise = ownProfileOnly
    ? sdk.ownListings.query({
        states: ['published'],
        ...queryParams,
      })
    : sdk.listings.query({
        author_id: userId,
        ...queryParams,
      });

  return listingsPromise
    .then(response => {
      // Pick only the id and type properties from the response listings
      const listings = response.data.data;
      const listingRefs = listings
        .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
        .map(({ id, type }) => ({ id, type }));
      dispatch(addMarketplaceEntities(response));
      return { listingRefs, response };
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const queryUserListingsThunk = createAsyncThunk(
  'ProfilePage/queryUserListings',
  queryUserListingsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const queryUserListings = (userId, config, ownProfileOnly = false) => dispatch => {
  return dispatch(queryUserListingsThunk({ userId, config, ownProfileOnly }));
};

//////////////////////////
// Query User's Reviews //
//////////////////////////
const queryUserReviewsPayloadCreator = ({ userId }, { rejectWithValue, extra: sdk }) => {
  return sdk.reviews
    .query({
      subject_id: userId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      return reviews;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const queryUserReviewsThunk = createAsyncThunk(
  'ProfilePage/queryUserReviews',
  queryUserReviewsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const queryUserReviews = userId => dispatch => {
  return dispatch(queryUserReviewsThunk({ userId }));
};

// ================ Slice ================ //

const initialState = {
  userId: null,
  userListingRefs: [],
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

const profilePageSlice = createSlice({
  name: 'ProfilePage',
  initialState,
  reducers: {
    setInitialState: () => initialState,
    setUserId: (state, action) => {
      state.userId = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // showUser cases
      .addCase(showUserThunk.pending, (state, action) => {
        state.userShowError = null;
        state.userId = action.meta.arg.userId;
      })
      .addCase(showUserThunk.fulfilled, state => {
        // No state changes needed on success
      })
      .addCase(showUserThunk.rejected, (state, action) => {
        state.userShowError = storableError(action.payload);
      })
      // queryUserListings cases
      .addCase(queryUserListingsThunk.pending, (state, action) => {
        const userId = action.meta.arg.userId;
        // Empty listings only when user id changes
        state.userListingRefs = userId === state.userId ? state.userListingRefs : [];
        state.queryListingsError = null;
      })
      .addCase(queryUserListingsThunk.fulfilled, (state, action) => {
        state.userListingRefs = action.payload.listingRefs;
      })
      .addCase(queryUserListingsThunk.rejected, (state, action) => {
        state.userListingRefs = [];
        state.queryListingsError = storableError(action.payload);
      })
      // queryUserReviews cases
      .addCase(queryUserReviewsThunk.pending, state => {
        state.queryReviewsError = null;
      })
      .addCase(queryUserReviewsThunk.fulfilled, (state, action) => {
        state.reviews = action.payload;
      })
      .addCase(queryUserReviewsThunk.rejected, (state, action) => {
        state.reviews = [];
        state.queryReviewsError = action.payload;
      });
  },
});

export const { setInitialState, setUserId } = profilePageSlice.actions;
export default profilePageSlice.reducer;

// ================ Load data ================ //

const isCurrentUser = (userId, cu) => userId?.uuid === cu?.id?.uuid;

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const userId = new UUID(params.id);
  const isPreviewForCurrentUser = params.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;
  const currentUser = getState()?.user?.currentUser;
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };

  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialState());

  if (isPreviewForCurrentUser) {
    return dispatch(fetchCurrentUser(fetchCurrentUserOptions)).then(() => {
      if (isCurrentUser(userId, currentUser) && isUserAuthorized(currentUser)) {
        // Scenario: 'active' user somehow tries to open a link for "variant" profile
        return Promise.all([
          dispatch(showUser(userId, config)),
          dispatch(queryUserListings(userId, config)),
          dispatch(queryUserReviews(userId)),
        ]);
      } else if (isCurrentUser(userId, currentUser)) {
        // Handle a scenario, where user (in pending-approval state)
        // tries to see their own profile page.
        // => just set userId to state
        return dispatch(setUserId(userId));
      } else {
        return Promise.resolve({});
      }
    });
  }

  // Fetch data for plain profile page.
  // Note 1: returns 404s if user is not 'active'.
  // Note 2: In private marketplace mode, this page won't fetch data if the user is unauthorized
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const canFetchData = !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);
  // On a private marketplace, show active (approved) current user's own page
  // even if they don't have viewing rights
  const canFetchOwnProfileOnly =
    isPrivateMarketplace &&
    isAuthorized &&
    hasNoViewingRights &&
    isCurrentUser(userId, currentUser);

  if (!canFetchData) {
    return Promise.resolve();
  } else if (canFetchOwnProfileOnly) {
    return Promise.all([
      dispatch(fetchCurrentUser(fetchCurrentUserOptions)),
      dispatch(queryUserListings(userId, config, canFetchOwnProfileOnly)),
      dispatch(setUserId(userId)),
    ]);
  }

  return Promise.all([
    dispatch(fetchCurrentUser(fetchCurrentUserOptions)),
    dispatch(showUser(userId, config)),
    dispatch(queryUserListings(userId, config)),
    dispatch(queryUserReviews(userId)),
  ]);
};


export const loadDataByUsername = (params, search, config) => (dispatch, getState, sdk) => {
  const username = params.username;
  const forceRefresh = search && search.includes('refresh=true');

  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialState());

  // First, try to get user ID from Redux store
  const state = getState();
  const userId = getUserIdByUsername(state, username);
  
  if (userId) {
    console.log(`Found user ID for username "${username}": ${userId} (from Redux store)`);
    // Use the existing loadData function with the found user ID
    const modifiedParams = { ...params, id: userId };
    return dispatch(loadData(modifiedParams, search, config));
  }

  // If not found in store, get username-to-ID mapping from Redux
  return getUsernameToIdMappingFromStore(dispatch, getState, forceRefresh)
    .then(response => {
      if (!response || !response.data) {
        console.error('Failed to fetch username mapping - invalid response');
        throw new Error('Failed to fetch username mapping');
      }

      const mappingSize = Object.keys(response.data).length;
      const currentState = getState();
      const cacheValid = isArtistUsernamesCacheValid(currentState);
      console.log(`Got username mapping with ${mappingSize} entries${cacheValid && !forceRefresh ? ' (from cache)' : ''}`);
      
      // Find the user ID corresponding to the username (case-insensitive)
      const userIdEntry = Object.entries(response.data).find(
        ([_, userData]) => {
          // userData can be an object with username property or a string
          if (typeof userData === 'object' && userData.username) {
            return userData.username.toLowerCase() === username.toLowerCase();
          } else if (typeof userData === 'string') {
            return userData.toLowerCase() === username.toLowerCase();
          }
          return false;
        }
      );

      if (!userIdEntry) {
        console.error(`User with username "${username}" not found in mapping`);
        
        // Instead of throwing an error, we'll fall back to the old method
        console.log('Falling back to old method of loading profile by username');
        return Promise.all([
          dispatch(fetchCurrentUser()),
          dispatch(queryUserListingsByUsername(username, config))
        ]);
      }

      const foundUserId = userIdEntry[0]; // The key is the user ID
      console.log(`Found user ID for username "${username}": ${foundUserId}`);
      
      // Use the existing loadData function with the found user ID
      // We're creating a modified params object with the id property
      const modifiedParams = { ...params, id: foundUserId };
      
      return dispatch(loadData(modifiedParams, search, config));
    })
    .catch(error => {
      console.error('Error loading profile by username:', error);
      
      // Fallback to the old method if username mapping fails
      console.log('Falling back to old method of loading profile by username');
      return Promise.all([
        dispatch(fetchCurrentUser()),
        dispatch(queryUserListingsByUsername(username, config))
      ]);
    });
};
